import React, {useEffect, useState} from 'react'
import 'bootstrap/dist/css/bootstrap.min.css'
import axios from 'axios'
import {API_ENDPOINTS} from '../../../../constants/apiConstants'
import {useReduxAuth} from '../../../auth'
import {HiOutlineEmojiSad} from 'react-icons/hi'
import {LuClock3} from 'react-icons/lu'
import {Link} from 'react-router-dom'
import ScrollWrapper from '../ScrollWrapper'
import {PatientModel} from '../../../../entities/entities'
import {ZeroStateCard, CardCategory} from '../../../../shared/components'
import PatientCard from '../common/PatientCard'
import moment from 'moment'
import {useMADoctorDropdown} from '../../context/MADoctorDropdownContext'
import {USER_ROLES} from '../../../../constants/StatusConstants'

// Helper to format "time ago" messages, showing "just now" for under 1 minute
const getTimeAgoMessage = (timestamp: string | null | undefined, label: string) => {
  if (!timestamp) return `${label} time unknown`

  const now = moment()
  // Treat the timestamp as regular datetime without timezone conversion
  const ts = moment(timestamp)
  // clamp future timestamps
  const diffMs = Math.max(now.diff(ts), 0)
  const duration = moment.duration(diffMs)
  const hours = duration.asHours()

  if (hours < 1) {
    const minutes = Math.floor(duration.asMinutes())
    if (minutes < 1) {
      return `${label} just now`
    }
    return `${label} ${minutes} min${minutes !== 1 ? 's' : ''} ago`
  } else {
    const roundedHours = Math.floor(hours)
    return `${label} ${roundedHours} hr${roundedHours !== 1 ? 's' : ''} ago`
  }
}

const UsageAlerts: React.FC = () => {
  const {currentUser} = useReduxAuth()
  const maDropdown = useMADoctorDropdown()
  const maSelectedDoctorId = currentUser?.role_id === USER_ROLES.MEDICAL_ASSISTANT ? maDropdown.selectedDoctorId : undefined
  
  const [latePatients, setLatePatients] = useState<PatientModel[]>([])
  const [noReadingPatients, setNoReadingPatients] = useState<PatientModel[]>([])

  useEffect(() => {
    const fetchData = async () => {
      // Skip if MA and no doctor selected
      if (currentUser?.role_id === USER_ROLES.MEDICAL_ASSISTANT && !maSelectedDoctorId) {
        setLatePatients([])
        setNoReadingPatients([])
        return
      }

      try {
        // For MA, use selected doctor ID; for others, use currentUser.id
        const doctorId = currentUser?.role_id === USER_ROLES.MEDICAL_ASSISTANT && maSelectedDoctorId
          ? maSelectedDoctorId
          : currentUser?.id

        if (!doctorId) return

        // Build query params for MA
        const queryParams = new URLSearchParams()
        if (currentUser?.role_id === USER_ROLES.MEDICAL_ASSISTANT && maSelectedDoctorId) {
          queryParams.append('doctor_id', maSelectedDoctorId.toString())
        }
        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Chicago'
        queryParams.append('timezone', timezone)

        const res = await axios.get(
          API_ENDPOINTS.GET_DOCTOR_PATIENTS_USAGE_ALERTS(doctorId, queryParams.toString())
        )
        
        setLatePatients(res.data?.data?.lateReading || [])
        // Combine both notSetup and noReading arrays
        const notSetupPatients = res.data?.data?.notSetup || []
        const noReadingPatients = res.data?.data?.noReading || []
        setNoReadingPatients([...notSetupPatients, ...noReadingPatients])
      } catch (error) {
        console.error('Error fetching usage alerts:', error)
      }
    }
    if (currentUser?.id || (currentUser?.role_id === USER_ROLES.MEDICAL_ASSISTANT && maSelectedDoctorId)) {
      fetchData()
    }
  }, [currentUser, maSelectedDoctorId])

  return (
    <div className='scrollListWrapper p-4 bg-white rounded-4 shadow-sm border h-100'>
      <h3>Usage Alerts</h3>

      <div className='scrollableList'>
        {/* First row – 48h+ late readings */}
        <div key='late' className='mt-3'>
          <div className='d-flex gap-2'>
            <div className='me-2'>
              <CardCategory
                label='Late'
                count={latePatients.length}
                className='bg-late'
                icon={<LuClock3 />}
              />
            </div>
            <div className='flex-grow-1 scroll-content'>
              <ScrollWrapper>
                {latePatients.length > 0 ? (
                  latePatients.map((patient) => (
                    <Link
                      to={`/patient-details/${patient.uuid}/health-metrics`}
                      key={patient.uuid}
                      style={{textDecoration: 'none', color: 'inherit'}}
                    >
                      <PatientCard
                        name={`${patient.first_name} ${patient.last_name}`}
                        phone={patient.phone_number || ''}
                        isReading={false}
                        message={getTimeAgoMessage(patient.last_reading_time, 'Last Reading')}
                        patientUUID={patient.uuid!} // <-- non-null assertion
                        onResolved={() => {}}
                      />
                    </Link>
                  ))
                ) : (
                  <ZeroStateCard />
                )}
              </ScrollWrapper>
            </div>
          </div>
        </div>

        {/* Second row – never taken readings */}
        <div key='not-setup' className='mt-3'>
          <div className='d-flex gap-2 scroll-content'>
            <div className='me-2'>
              <CardCategory
                label='No Reading Or Device'
                count={noReadingPatients.length}
                className='bg-not-setup'
                icon={<HiOutlineEmojiSad />}
              />
            </div>
          <div className=' scroll-wrapper flex-grow-1 scroll-content' style={{overflow: 'hidden'}}>
              <ScrollWrapper>
                {noReadingPatients.length > 0 ? (
                  noReadingPatients.map((patient) => (
                    <Link
                      to={`/patient-details/${patient.uuid}/health-metrics`}
                      key={patient.uuid}
                      style={{textDecoration: 'none', color: 'inherit'}}
                    >
                      <PatientCard
                        name={`${patient.first_name} ${patient.last_name}`}
                        phone={patient.phone_number || ''}
                        isReading={false}
                        message={
                          patient.allocationDate
                            ? getTimeAgoMessage(patient.allocationDate, 'Assigned')
                            : 'No device assigned!'
                        }
                        patientUUID={patient.uuid!} // <-- non-null assertion
                        onResolved={() => {}}
                      />
                    </Link>
                  ))
                ) : (
                  <ZeroStateCard />
                )}
              </ScrollWrapper>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default UsageAlerts
